import { useCallback, useEffect, useRef, useState } from 'react';
import { playSound, Sound } from '~/assets/sounds';
import { Text } from '~/components/ui/Text';
import { IVehicleMarketTuningItem } from '~/shared';
import { TuningCategoryIcons, TuningRarityColors } from './TuningRow.model';
import {
	StyledBadge,
	StyledBadgeDivider,
	StyledBadgeIcon,
	StyledBadgeLevel,
	StyledContainer,
	StyledNavButton,
	StyledScroll,
} from './TuningRow.styled';

const SCROLL_STEP = 246;
const BTN = 40;
const RAMP = BTN + 50;

const TuningRow = ({ items }: { items: IVehicleMarketTuningItem[] }) => {
	const scrollRef = useRef<HTMLDivElement>(null);
	const [canScrollLeft, setCanScrollLeft] = useState(false);
	const [canScrollRight, setCanScrollRight] = useState(false);

	const updateScrollState = useCallback(() => {
		const el = scrollRef.current;
		if (!el) return;
		setCanScrollLeft(el.scrollLeft > 1);
		setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
	}, []);

	useEffect(() => {
		updateScrollState();
	}, [items, updateScrollState]);

	const scrollTo = (direction: 1 | -1) => {
		const el = scrollRef.current;
		if (!el) return;

		const maxScroll = el.scrollWidth - el.clientWidth;
		const target = el.scrollLeft + direction * SCROLL_STEP;
		const left =
			direction === 1
				? maxScroll - target < SCROLL_STEP
					? maxScroll
					: target
				: target < SCROLL_STEP
				? 0
				: target;

		el.scrollTo({ left, behavior: 'smooth' });
		playSound(Sound.ClickButton);
	};

	if (!items.length) return null;

	return (
		<StyledContainer>
			<StyledScroll
				ref={scrollRef}
				onScroll={updateScrollState}
				style={{
					['--leftFlat' as any]: canScrollLeft ? `${BTN}px` : '0px',
					['--leftRamp' as any]: canScrollLeft ? `${RAMP}px` : '0px',
					['--rightFlat' as any]: canScrollRight ? `${BTN}px` : '0px',
					['--rightRamp' as any]: canScrollRight ? `${RAMP}px` : '0px',
				}}
			>
				{items.map((item, index) => {
					const active = item.rarity !== null;
					const color = item.rarity ? TuningRarityColors[item.rarity] : null;
					return (
						<StyledBadge key={`${item.category}-${index}`} $color={color}>
							<StyledBadgeIcon
								src={TuningCategoryIcons[item.category]}
								$active={active}
								alt=""
							/>
							<StyledBadgeDivider />
							<StyledBadgeLevel $active={active}>
								<Text>{item.level ?? '-'}</Text>
							</StyledBadgeLevel>
						</StyledBadge>
					);
				})}
			</StyledScroll>
			<StyledNavButton
				$side="left"
				$visible={canScrollLeft}
				onClick={() => scrollTo(-1)}
				onMouseEnter={() => playSound(Sound.LinkCursor)}
			>
				<div />
			</StyledNavButton>
			<StyledNavButton
				$side="right"
				$visible={canScrollRight}
				onClick={() => scrollTo(1)}
				onMouseEnter={() => playSound(Sound.LinkCursor)}
			>
				<div />
			</StyledNavButton>
		</StyledContainer>
	);
};

export { TuningRow };
